/**
 * InfinitSales - Pagina do Messenger conectada (ver/desconectar)
 *
 * Espelho do InstagramAccountModal para o canal Messenger: mostra qual Pagina
 * do Facebook esta ativa e permite desconectar. O fluxo de CONECTAR (Facebook
 * Login ou token do painel) vive no MessengerConnectionModal, dentro do inbox
 * — quando nao ha conta, este modal encaminha para /messenger pedindo a
 * abertura do modal de conexao via state.openConnection.
 *
 * Desconectar tem duas forcas, espelhando a API (DELETE /account?purge=):
 * sem purge a conta apenas para de operar e o historico volta ao reconectar;
 * com purge as conversas sao apagadas de vez — por isso o checkbox explicito.
 */
import React, { useState, useEffect } from 'react';
import {
  X,
  Facebook,
  Loader2,
  AlertTriangle,
  LogOut,
  Wifi,
  Hash,
  CalendarClock,
  Plug,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import messengerService from '../../services/messenger/messengerService';

const MS_BLUE = '#0084ff';

const MessengerAccountModal = ({ onClose, onDisconnected, onRequestConnect }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  // Espelho (Messenger da equipe): membro operando a Pagina do ADMIN — o modal
  // vira somente-leitura (desconectar e acao do dono da conexao).
  const [mirror, setMirror] = useState(false);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [purge, setPurge] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await messengerService.getStatus();
        if (cancelled) return;
        const data = status?.data || {};
        setConnected(!!data.connected);
        setMirror(!!data.mirror);
        if (data.connected) {
          // O /account traz o doc completo (conectada em, validade do token);
          // se falhar, o proprio /status ja tem o essencial (Pagina, foto).
          try {
            const acc = await messengerService.getAccount();
            if (!cancelled) setAccount(acc?.data || data);
          } catch {
            if (!cancelled) setAccount(data);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setConnected(false);
          if (e?.response?.status !== 404) {
            setError('Não foi possível consultar o status da conexão.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await messengerService.disconnect(purge);
      onDisconnected?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'object' ? detail?.message : detail)
        || 'Erro ao desconectar a Página.'
      );
      setShowConfirm(false);
      setDisconnecting(false);
    }
  };

  // CTA de conectar: quem monta o modal pode passar onRequestConnect (padrão
  // do NavSidebar); sem a prop, navega direto para o inbox pedindo o modal.
  const handleRequestConnect = () => {
    if (onRequestConnect) {
      onRequestConnect();
      return;
    }
    onClose?.();
    navigate('/messenger', { state: { openConnection: true } });
  };

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR');
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: 16, maxWidth: 440,
      width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    closeBtn: {
      position: 'absolute', top: 16, right: 16, width: 36, height: 36,
      border: 'none', background: colors.bgTertiary, color: colors.textSecondary,
      cursor: 'pointer', borderRadius: '50%', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10,
    },
    content: { padding: '40px 32px' },
    container: { textAlign: 'center' },
    iconBox: {
      width: 80, height: 80, backgroundColor: `${MS_BLUE}15`, borderRadius: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px', position: 'relative',
    },
    connectedBadge: {
      position: 'absolute', bottom: -4, right: -4, width: 28, height: 28,
      backgroundColor: MS_BLUE, borderRadius: '50%', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      border: `3px solid ${colors.bgPrimary}`,
    },
    title: {
      fontSize: 22, fontWeight: 600, color: colors.textPrimary, marginBottom: 8,
    },
    subtitle: {
      fontSize: 14, color: colors.textSecondary, marginBottom: 24,
    },
    accountCard: {
      backgroundColor: colors.bgSecondary, borderRadius: 12, padding: 20,
      marginBottom: 24, textAlign: 'left',
    },
    accountRow: {
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
    },
    accountRowLast: {
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0,
    },
    accountIcon: {
      width: 36, height: 36, borderRadius: 8, backgroundColor: colors.bgTertiary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: colors.textSecondary, overflow: 'hidden', flexShrink: 0,
    },
    accountLabel: { fontSize: 12, color: colors.textTertiary, marginBottom: 2 },
    accountValue: {
      fontSize: 15, fontWeight: 500, color: colors.textPrimary,
      overflowWrap: 'anywhere',
    },
    errorBox: {
      backgroundColor: `${colors.danger || '#ef4444'}15`,
      border: `1px solid ${colors.danger || '#ef4444'}30`,
      borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex',
      alignItems: 'flex-start', gap: 12, textAlign: 'left',
    },
    errorText: {
      fontSize: 14, color: colors.danger || '#ef4444', margin: 0, flex: 1,
      lineHeight: 1.4,
    },
    disconnectBtn: {
      width: '100%', padding: '14px 24px', backgroundColor: 'transparent',
      border: `1px solid ${colors.danger || '#ef4444'}`,
      color: colors.danger || '#ef4444', borderRadius: 12, fontSize: 15,
      fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10, transition: 'all 0.2s',
    },
    connectActionBtn: {
      width: '100%', padding: '14px 24px', backgroundColor: MS_BLUE,
      border: 'none', color: '#fff', borderRadius: 12, fontSize: 15,
      fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10,
    },
    footerNote: {
      marginTop: 20, fontSize: 12, color: colors.textTertiary,
    },
    confirmOverlay: {
      position: 'absolute', inset: 0, backgroundColor: colors.bgPrimary,
      borderRadius: 16, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 36, zIndex: 10,
    },
    confirmIcon: {
      width: 64, height: 64,
      backgroundColor: `${colors.danger || '#ef4444'}15`, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 20,
    },
    confirmTitle: {
      fontSize: 20, fontWeight: 600, color: colors.textPrimary, marginBottom: 8,
      textAlign: 'center',
    },
    confirmText: {
      fontSize: 14, color: colors.textSecondary, textAlign: 'center',
      marginBottom: 20, lineHeight: 1.5,
    },
    purgeRow: {
      display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24,
      padding: '12px 14px', borderRadius: 10,
      backgroundColor: colors.bgSecondary, cursor: 'pointer',
      fontSize: 13, color: colors.textPrimary, lineHeight: 1.4, textAlign: 'left',
    },
    confirmButtons: { display: 'flex', gap: 12, width: '100%' },
    confirmCancelBtn: {
      flex: 1, padding: '12px 20px', backgroundColor: colors.bgTertiary,
      border: 'none', color: colors.textPrimary, borderRadius: 10, fontSize: 14,
      fontWeight: 500, cursor: 'pointer',
    },
    confirmDisconnectBtn: {
      flex: 1, padding: '12px 20px',
      backgroundColor: colors.danger || '#ef4444', border: 'none',
      color: 'white', borderRadius: 10, fontSize: 14, fontWeight: 500,
      cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 8,
    },
    loadingBox: {
      padding: '60px 0', display: 'flex', alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const tokenExpiry = formatDate(account?.token_expires_at);
  const connectedAt = formatDate(account?.connected_at || account?.created_at);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingBox}>
              <Loader2
                size={32}
                color={MS_BLUE}
                style={{ animation: 'spin 1s linear infinite' }}
              />
            </div>
          ) : connected ? (
            <div style={styles.container}>
              <div style={styles.iconBox}>
                <Facebook size={40} color={MS_BLUE} />
                <div style={styles.connectedBadge}>
                  <Wifi size={14} color="white" />
                </div>
              </div>
              <h2 style={styles.title}>Messenger Conectado</h2>
              <p style={styles.subtitle}>
                {mirror
                  ? 'Página da empresa, conectada pelo administrador. Você atende as conversas atribuídas a você.'
                  : 'Sua Página está ativa e recebendo mensagens'}
              </p>

              <div style={styles.accountCard}>
                <div style={styles.accountRow}>
                  <div style={styles.accountIcon}>
                    {account?.profile_picture_url ? (
                      <img
                        src={account.profile_picture_url}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Facebook size={18} />
                    )}
                  </div>
                  <div>
                    <div style={styles.accountLabel}>Página</div>
                    <div style={styles.accountValue}>
                      {account?.page_name || account?.page_id}
                    </div>
                  </div>
                </div>

                {account?.page_id && (
                  <div style={styles.accountRow}>
                    <div style={styles.accountIcon}>
                      <Hash size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>ID da Página</div>
                      <div style={styles.accountValue}>{account.page_id}</div>
                    </div>
                  </div>
                )}

                {(tokenExpiry || connectedAt) && (
                  <div style={styles.accountRowLast}>
                    <div style={styles.accountIcon}>
                      <CalendarClock size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>
                        {tokenExpiry ? 'Token válido até' : 'Conectada em'}
                      </div>
                      <div style={styles.accountValue}>
                        {tokenExpiry || connectedAt}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div style={styles.errorBox}>
                  <AlertTriangle
                    size={18}
                    color={colors.danger || '#ef4444'}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <p style={styles.errorText}>{error}</p>
                </div>
              )}

              {!mirror && (
                <>
                  <button
                    style={styles.disconnectBtn}
                    onClick={() => setShowConfirm(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.danger || '#ef4444'}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LogOut size={18} />
                    Desconectar Página
                  </button>

                  <p style={styles.footerNote}>
                    Ao desconectar, as mensagens do Messenger param de chegar
                    nesta plataforma.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div style={styles.container}>
              <div style={styles.iconBox}>
                <Facebook size={40} color={MS_BLUE} />
              </div>
              <h2 style={styles.title}>Messenger não conectado</h2>
              <p style={styles.subtitle}>
                Conecte uma Página do Facebook para receber as mensagens do
                Messenger aqui
              </p>

              {error && (
                <div style={styles.errorBox}>
                  <AlertTriangle
                    size={18}
                    color={colors.danger || '#ef4444'}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <p style={styles.errorText}>{error}</p>
                </div>
              )}

              <button style={styles.connectActionBtn} onClick={handleRequestConnect}>
                <Plug size={16} />
                Conectar Messenger
              </button>
            </div>
          )}

          {showConfirm && (
            <div style={styles.confirmOverlay}>
              <div style={styles.confirmIcon}>
                <AlertTriangle size={32} color={colors.danger || '#ef4444'} />
              </div>
              <h3 style={styles.confirmTitle}>Desconectar Messenger?</h3>
              <p style={styles.confirmText}>
                {account?.page_name ? `A Página ${account.page_name}` : 'A Página'}
                {' '}vai parar de enviar e receber mensagens pela plataforma.
                Sem apagar o histórico, as conversas voltam quando ela
                reconectar.
              </p>

              <label style={styles.purgeRow}>
                <input
                  type="checkbox"
                  checked={purge}
                  onChange={(e) => setPurge(e.target.checked)}
                  disabled={disconnecting}
                  style={{ marginTop: 2, accentColor: colors.danger || '#ef4444' }}
                />
                <span>Apagar também as conversas e mensagens (irreversível)</span>
              </label>

              <div style={styles.confirmButtons}>
                <button
                  style={styles.confirmCancelBtn}
                  onClick={() => setShowConfirm(false)}
                  disabled={disconnecting}
                >
                  Cancelar
                </button>
                <button
                  style={{
                    ...styles.confirmDisconnectBtn,
                    ...(disconnecting ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <LogOut size={16} />
                      Desconectar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MessengerAccountModal;
